import { useContext, useMemo, useState } from "react";
import { SessionContext } from "../../common/contexts/SessionContext";
import { LucideIcon } from "../../common/icon/LucideIcon";
import TableView, { TableViewLabel } from "../../common/panels/TableView/TableView";
import { ColumnsSettings } from "../../common/tables/ColumnsSelector/ColumnsSelector";
import { AlertModal } from "../../common/modals/AlertModal/AlertModal";
import { MoreDropdown } from "../../common/buttons/MoreDropdown/MoreDropdown";
import { EntityTable } from "../../common/tables/EntityTable/EntityTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { EntityFilterIndicator } from "../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { EntityTableTabs } from "../../common/tables/Tabs/EntityTableTabs";
import { useTabStore } from "../../common/tables/Tabs/useTabStore";
import { TableTabsDict } from "../../common/tables/Tabs/TableTabsTypes";
import {
  LoginTypesTranslator,
  Person,
  PersonFilters,
  PersonFiltersTranslator,
  PersonSearchTermOptions,
} from "../../api/Person";
import { PersonFilterBar } from "./PersonFilterBar";
import { PersonFilterForm } from "./PersonFilterBar";
import { NotAvailable, NotSet } from "../../common/misc/UIconstants";
import Status from "../../common/badges/Status/Status";
import { DateTimeRenderer } from "../../common/datetime/DateTimeFormatter";
import { GetPersons } from "../../common/misc/EntityRenders/EntityRenderer";
import { AddCustomTypeEntity } from "../../Customization/CustomTypes/generics/AddCustomTypeEntity";
import { useCustomTypesEntityTable } from "../../Customization/CustomTypes/generics/useCustomTypesEntityTable";
import { TwoFactorAuthIndicator } from "../TotpModal";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableDefaultProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
  UseITypedEntityTableProps,
} from "../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { renderCustomTypeColumn } from "../../Customization/CustomTypes/generics/CustomTypeRenderer";
import { EntityTableProps } from "../../common/entity/EntityInterfaces";
import { EntityTableEditButton } from "../../common/entity/entityComponents/EntityTableEditButton";
import { EntityTableCloneButton } from "../../common/entity/entityComponents/EntityTableCloneButton";
import { EntityTableRestoreButton } from "../../common/entity/entityComponents/EntityTableRestoreButton";
import { EntityTableSoftDeletableButton } from "../../common/entity/entityComponents/EntityTableSoftDeletableButton";
import { Button } from "../../common/buttons/Button/Button";
import { useUnpaginateEntities } from "../../common/forms/MultiEditForms/common/MultiEditUtils";
import { GenericEntityTableComplianceDropdown } from "../../Entities/GenericComponents/common/GenericEntityTable";
import { ExportCsvButton } from "../../common/tables/ExportCsvButton/ExportCsvButton";
import SearchInputWithOptions from "../../common/forms/SearchInputWithOptions/SearchInputWithOptions";
import { NestedDropdown } from "../../common/buttons/NestedDropdown/NestedDropdown";
import { ExportPdfButton } from "../../common/tables/ExportPdfButton/ExportPdfButton";
import { EntityOrderBy, InferSortByOptions, ISearchTermParametersDefaults } from "../../api/GenericTypes";

export const switchPersonsDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<PersonFilters>>[] | null | undefined
): SortState<InferSortByOptions<PersonFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;
  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "LAST_NAME":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-lastName",
          property: sort.property,
        };

      case "FIRST_NAME":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-firstName",
          property: sort.property,
        };

      case "LOGIN_DISABLED":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-loginDisabled",
          property: sort.property,
        };

      case "LOGIN":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-login",
          property: sort.property,
        };

      default:
        return {
          sortDirection: "ASC",
          headerId: "default-name",
          property: "NAME",
        };
    }
  });
};

export const defaultPersonsFilters: PersonFilters = {
  sortBy: [
    {
      property: "NAME",
      sortDirection: "ASC",
    },
  ],
  ...ISearchTermParametersDefaults,
};

export const usePersonsTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"persons">) => {
  const { session } = useContext(SessionContext);
  const defaults: ColumnsSettings<Person> = useMemo(
    () => ({
      "default-id": { pos: 1, active: false, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 2, active: true, header: fieldLabels.name, property: "name" },
      "default-email": { pos: 3, active: true, header: fieldLabels.email, property: "email" },
      "default-lastName": { pos: 4, active: false, header: fieldLabels.lastName, property: "lastName" },
      "default-firstName": { pos: 5, active: false, header: fieldLabels.firstName, property: "firstName" },
      "default-login": { pos: 6, active: true, header: fieldLabels.login, property: "login" },
      "default-loginDisabled": {
        pos: 7,
        active: true,
        header: fieldLabels.accountState,
        property: "accountState",
      },
      "default-loginType": {
        pos: 8,
        active: true,
        header: fieldLabels.loginType,
        property: "loginType",
      },
      ...(session?.features.enable_2fa && {
        "default-has2FA": { pos: 9, active: true, header: fieldLabels.has2FA, property: "has2FA" },
      }),
      "default-roles": { pos: 10, active: true, header: fieldLabels.roles, property: "roles" },
      "default-customType": { pos: 11, active: true, header: fieldLabels.customType, property: "customType" },
      "default-enteredOn": { pos: 12, active: true, header: fieldLabels.enteredOn, property: "enteredOn" },
      "default-enteredBy": { pos: 13, active: true, header: fieldLabels.enteredBy, property: "enteredBy" },
      "default-modifiedOn": { pos: 14, active: true, header: fieldLabels.modifiedOn, property: "modifiedOn" },
      "default-modifiedBy": { pos: 15, active: true, header: fieldLabels.modifiedBy, property: "modifiedBy" },
    }),
    [
      fieldLabels.id,
      fieldLabels.name,
      fieldLabels.customType,
      fieldLabels.lastName,
      fieldLabels.firstName,
      fieldLabels.email,
      fieldLabels.login,
      fieldLabels.accountState,
      fieldLabels.loginType,
      fieldLabels.has2FA,
      fieldLabels.roles,
      fieldLabels.enteredOn,
      fieldLabels.enteredBy,
      fieldLabels.modifiedOn,
      fieldLabels.modifiedBy,
      session?.features.enable_2fa,
    ]
  );

  const tabStoreDefaults: TableTabsDict<Person, PersonFilters, PersonFilterForm> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultPersonsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
      activeUsers: {
        tabId: "activeUsers",
        type: "fixed",
        label: "Enabled accounts",
        title: "Enabled accounts",
        icon: "user-check",
        align: "left",
        xPos: 1,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultPersonsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { hasAccount: true, isAccountEnabled: true },
          sidebarFilters: {},
        },
      },
      disabledUsers: {
        tabId: "disabledUsers",
        type: "fixed",
        label: "Disabled accounts",
        title: "Disabled accounts",
        icon: "user-x",
        align: "left",
        xPos: 2,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultPersonsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { hasAccount: true, isAccountEnabled: false },
          sidebarFilters: {},
        },
      },
      trash: {
        tabId: "trash",
        type: "fixed",
        label: "Trash",
        title: "Trash",
        icon: "trash",
        align: "left",
        xPos: 3,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultPersonsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { isSoftDeleted: true },
          sidebarFilters: {},
        },
      },
    }),
    []
  );

  return { defaults, tabStoreDefaults };
};

export const usePersonsTableColumns = ({
  entityConstants,
  fieldLabels,
  defaults,
  filters,
  sort,
  setSort,
}: UseITypedEntityTableProps<"persons">) => {
  // Custom Fields
  const { columnSettings, customFieldHeaders } = useCustomTypesEntityTable({
    defaultColumnSettings: defaults,
    entityType: "Person",
    customTypeIds: filters?.customTypeIds ?? undefined,
    sort,
    setSort,
  });

  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<Person> = [
      {
        id: "default-id",
        Header: "Person-ID",
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 200,
        minWidth: 100,
        align: "center",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.name,
        id: "default-name",
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
            title={row.login ? (row.accountState === "Disabled" ? `${row.login} (account disabled)` : row.login) : ""}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon
                  name={row.login ? (row.accountState === "Disabled" ? "user-x" : "user-check") : "user"}
                  color="var(--primary)"
                />{" "}
                {row.name}
              </span>
            </div>
          </div>
        ),
        minWidth: 100,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        Header: fieldLabels.lastName,
        id: "default-lastName",
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>{row.lastName}</span>
            </div>
          </div>
        ),
        minWidth: 100,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("LAST_NAME", sort, setSort),
      },
      {
        Header: fieldLabels.firstName,
        id: "default-firstName",
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>{row.firstName}</span>
            </div>
          </div>
        ),
        minWidth: 100,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("FIRST_NAME", sort, setSort),
      },
      {
        Header: fieldLabels.email,
        id: "default-email",
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.email ? (
                <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>{row.email}</span>
              ) : (
                NotSet
              )}
            </div>
          </div>
        ),
        minWidth: 100,
        width: 200,
        align: "left",
      },
      {
        Header: fieldLabels.roles,
        id: "default-roles",
        accessor: (row) => (
          <div
            style={{
              display: "flex",
              flexFlow: "row nowrap",
              gap: "5px",
              overflow: "hidden",
              overflowX: "auto",
              alignItems: "center",
            }}
          >
            {!!row.roles?.length
              ? row.roles.map((c, i) => (
                  <span
                    key={i}
                    style={{ margin: "0 2px", overflow: "hidden", textOverflow: "ellipsis" }}
                    className="label label-soft-secondary"
                  >
                    {c.name}
                  </span>
                ))
              : NotSet}
          </div>
        ),
        minWidth: 100,
        width: 200,
        align: "left",
      },
      {
        Header: fieldLabels.has2FA,
        id: "default-has2FA",
        accessor: (row) => (
          <>{row.loginType !== "Session" ? NotAvailable : <TwoFactorAuthIndicator enabled={!!row.has2FA} />}</>
        ),

        minWidth: 100,
        width: 200,
        align: "left",
      },
      {
        Header: fieldLabels.accountState,
        id: "default-loginDisabled",
        accessor: (row) => (
          <>
            {!!row.login ? (
              <span className="flex row-nowrap align-center gap-5 badge" title={row.login || ""}>
                <Status type={row.accountState === "Disabled" ? "danger" : "success"} />
                {row.accountState === "Disabled" ? "Disabled" : "Enabled"}
              </span>
            ) : (
              NotSet
            )}
          </>
        ),
        minWidth: 100,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("LOGIN_DISABLED", sort, setSort),
      },
      {
        Header: fieldLabels.loginType,
        id: "default-loginType",
        accessor: (row) =>
          !!row.login && !!row.loginType ? (
            <label
              style={{
                margin: 0,
                lineHeight: undefined,
              }}
            >
              {LoginTypesTranslator[row.loginType]}
            </label>
          ) : (
            NotSet
          ),
        minWidth: 100,
        width: 200,
        align: "left",
      },
      {
        Header: fieldLabels.login,
        id: "default-login",
        accessor: (row) =>
          row.login ? (
            <div className="flex align-center gap-5">
              <div
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  marginRight: "5px",
                }}
              >
                {row.login}
              </div>
            </div>
          ) : (
            NotSet
          ),
        minWidth: 100,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("LOGIN", sort, setSort),
      },
      {
        id: "default-enteredOn",
        Header: fieldLabels.enteredOn,
        accessor: (row) => <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_ON", sort, setSort),
      },
      {
        id: "default-enteredBy",
        Header: fieldLabels.enteredBy,
        accessor: (row) => <GetPersons persons={row.enteredBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_BY", sort, setSort),
      },
      {
        id: "default-modifiedOn",
        Header: fieldLabels.modifiedOn,
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        id: "default-modifiedBy",
        Header: fieldLabels.modifiedBy,
        accessor: (row) => <GetPersons persons={row.modifiedBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_BY", sort, setSort),
      },
    ];
    // Add custom field headers
    headers.push(renderCustomTypeColumn(entityConstants, sort, setSort));
    headers = headers.concat(customFieldHeaders);

    return headers;
  }, [
    customFieldHeaders,
    entityConstants,
    fieldLabels.accountState,
    fieldLabels.enteredBy,
    fieldLabels.enteredOn,
    fieldLabels.email,
    fieldLabels.firstName,
    fieldLabels.has2FA,
    fieldLabels.loginType,
    fieldLabels.lastName,
    fieldLabels.login,
    fieldLabels.modifiedBy,
    fieldLabels.modifiedOn,
    fieldLabels.name,
    fieldLabels.roles,
    setSort,
    sort,
  ]);

  return { columns, columnSettings };
};

export const PersonsTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
}: EntityTableProps<"persons">) => {
  const [showModal, setShowModal] = useState(false);

  const {
    bulkEditMutationAsync,
    bulkRestoreMutationAsync: bulkRestorePersons,
    bulkDeleteMutationAsync: deletePerson,
    isLoadingBulkEditMutation,
    isLoadingBulkRestoreMutation,
    isLoadingBulkDeleteMutation,
  } = entityApi;
  const { unpaginateEntities } = useUnpaginateEntities({ resource: entityConstants.resource });

  const { defaults, tabStoreDefaults } = usePersonsTableDefaults({ fieldLabels });

  const { selection, resultsCount, selectionPermissions, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<Person>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<Person, PersonFilters, PersonFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, onTabChange, sort, setSort, searchValue, setSearchValue, debouncedSearchValue } =
    useGenericVirtualizedTableTabs({
      currentTab,
      tabsLoading,
      filters,
      switchSortState: switchPersonsDefaultSortState,
      dispatchTabStore,
    });

  const { columns, columnSettings } = usePersonsTableColumns({
    fieldLabels,
    entityConstants,
    defaults,
    filters,
    sort,
    setSort,
  });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
        <TableView.Head.Controls>
          <AddCustomTypeEntity
            entityConstants={entityConstants}
            canCreateEntityPermission={permissions.canCreate}
            entityType={"Person"}
          />
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar>
          <PersonFilterBar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            temporaryTabs={temporaryTabs}
            fixedTabs={fixedTabs}
            filters={filters}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
          />
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<Person, PersonFilters>
                filters={filters}
                excludeFilters={{
                  includeSystemUsers: (value) => !!value,
                  hasAccount: (value) =>
                    currentTab !== "activeUsers" && currentTab !== "disabledUsers" ? !!value : false,
                  isAccountEnabled: (value) =>
                    currentTab !== "activeUsers" && currentTab !== "disabledUsers" ? !!value : false,
                  includeSoftDeleted: (value) => !!value,
                  isSoftDeleted: (value) => !!value,
                  searchTermIncludeIds: () => false,
                  searchTermIncludeNames: () => false,
                  searchTermIncludeUuids: () => false,
                  searchTermIncludeCustomFieldValues: () => false,
                }}
                translatorConsts={PersonFiltersTranslator}
              />
              <SearchInputWithOptions
                searchTermOptions={PersonSearchTermOptions}
                filters={filters}
                setFilters={async (newFilters) => {
                  await dispatchTabStore({
                    type: "setTab",
                    options: { keepPrevious: true },
                    payload: { settings: { filters: { searchTerm: debouncedSearchValue, ...newFilters } } },
                  });
                }}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
              />
              {/* <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" /> */}
              <div
                className="flex row-nowrap align-center gap-5"
                style={{ height: "min-content", marginBottom: "auto" }}
              >
                <EntityTableEditButton
                  entityConstants={entityConstants}
                  routes={routes}
                  permissions={permissions}
                  selection={selection}
                  selectionPermissions={selectionPermissions}
                />
                <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                  <li>
                    <EntityTableCloneButton
                      entityConstants={entityConstants}
                      routes={routes}
                      permissions={permissions}
                      selection={selection}
                    />
                  </li>
                  <NestedDropdown label="Share / Export" icon="share-2" disabled={selection.size === 0}>
                    <li>
                      <ExportCsvButton
                        entityConstants={entityConstants}
                        columnSettings={columnSetting}
                        filters={{
                          ids: selection.size > 0 ? Array.from(selection) : undefined,
                          ...memoizedFilters,
                          searchTerm: debouncedSearchValue,
                        }}
                        disabled={selection.size === 0}
                      />
                    </li>
                    <li>
                      <ExportPdfButton
                        entityConstants={entityConstants}
                        filters={{
                          ids: selection.size > 0 ? Array.from(selection) : undefined,
                          ...memoizedFilters,
                          searchTerm: debouncedSearchValue,
                        }}
                        disabled={selection.size === 0}
                      />
                    </li>
                  </NestedDropdown>
                  {/* Locking & Signing */}
                  <GenericEntityTableComplianceDropdown
                    entityTypeId={entityTypeId}
                    selection={selection}
                    selectionPermissions={selectionPermissions}
                    tableFunctionRef={functionRef}
                    currentTab={currentTab}
                  />
                  {currentTab !== "trash" && (
                    <>
                      {currentTab !== "activeUsers" && (
                        <li>
                          <Button
                            className="btn btn-sm btn-ghost-success"
                            disabled={!permissions.canBulkEdit(selectionPermissions)}
                            loading={isLoadingBulkEditMutation}
                            onClick={async () => {
                              const entities = await unpaginateEntities<Person>(Array.from(selection));
                              await bulkEditMutationAsync({
                                body: Object.values(entities).map((e) => ({ ...e, accountState: "Enabled" })),
                              }).catch(() => {});
                            }}
                          >
                            <LucideIcon name="toggle-right" /> Enable
                          </Button>
                        </li>
                      )}
                      {currentTab !== "disabledUsers" && (
                        <li>
                          <Button
                            className="btn btn-sm btn-ghost-danger"
                            disabled={!permissions.canBulkEdit(selectionPermissions)}
                            loading={isLoadingBulkEditMutation}
                            onClick={async () => {
                              const entities = await unpaginateEntities<Person>(Array.from(selection));
                              await bulkEditMutationAsync({
                                body: Object.values(entities).map((e) => ({ ...e, accountState: "Disabled" })),
                              }).catch(() => {});
                            }}
                          >
                            <LucideIcon name="toggle-left" /> Disable
                          </Button>
                        </li>
                      )}
                    </>
                  )}
                  <li className="divider" />
                  {currentTab === "trash" && (
                    <li>
                      <EntityTableRestoreButton
                        entityConstants={entityConstants}
                        selection={selection}
                        permissions={permissions}
                        selectionPermissions={selectionPermissions}
                        onClick={async () => await bulkRestorePersons({ ids: Array.from(selection) }).catch(() => {})}
                        loading={isLoadingBulkRestoreMutation}
                      />
                    </li>
                  )}
                  <li>
                    <EntityTableSoftDeletableButton
                      currentTab={currentTab}
                      entityConstants={entityConstants}
                      selection={selection}
                      permissions={permissions}
                      selectionPermissions={selectionPermissions}
                      onClick={() => setShowModal(true)}
                    />
                  </li>
                </MoreDropdown>
              </div>
              <AlertModal
                type={`${currentTab === "trash" ? "danger" : "warning"}`}
                showModal={showModal}
                setShowModal={setShowModal}
                title={`${
                  selection.size === 0
                    ? `Select dataset to ${currentTab === "trash" ? "delete" : "trash"}`
                    : selection.size === 1
                    ? `${currentTab === "trash" ? "Delete" : "Trash"} the selected ${entityConstants.entitySingular}?`
                    : `${currentTab === "trash" ? "Delete" : "Trash"} the selected ${entityConstants.entityPlural}?`
                }`}
                description={`${
                  currentTab === "trash"
                    ? `Proceeding will permanently delete the selected ${entityConstants.entityPlural}.`
                    : `Proceeding will move the selected ${entityConstants.entityPlural} into trash.`
                }`}
                proceedLabel={`${currentTab === "trash" ? "Delete" : "Trash"}`}
                onProceed={async () => {
                  await deletePerson({
                    ids: Array.from(selection),
                    goBackOnSuccess: false,
                    showToast: false,
                    entityName: entityConstants.resource,
                    params: { deletePermanently: currentTab === "trash" },
                  }).catch((e) => {});
                  functionRef.current?.resetSelection();
                  setShowModal(false);
                }}
                loading={isLoadingBulkDeleteMutation}
              />
            </EntityTable.Controls>
            <EntityTable.Body<Person, PersonFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={columnSettings}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
